import { Badge } from '@/components/ui/badge'
import { fDateTime } from '@/utils/format-date'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import type { Trend } from './types'

type Props = {
  trend: Trend | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function TrendDetailDialog({
  trend,
  open,
  onOpenChange,
}: Props) {
  if (!trend) return null

  // Admin preview source. Prefer the direct MP4 (plays natively in every
  // browser); the HLS master is only a last resort (Safari plays it natively;
  // Chrome would need hls.js, which the admin intentionally does not bundle —
  // same choice as the clips preview). Transcoded trends always carry an MP4
  // fallback, so this reaches a playable source for every real trend.
  const playUrl =
    trend.fallbackMp4Url || trend.videoUrl || trend.hlsMasterUrl || ''

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>รายละเอียดเทรนด์</DialogTitle>
          <DialogDescription>{trend.id}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 md:grid-cols-[280px_1fr]">
          <div className="bg-black rounded-lg overflow-hidden aspect-[9/16]">
            {playUrl ? (
              <video
                key={trend.id}
                src={playUrl}
                poster={trend.thumbnailUrl}
                controls
                playsInline
                preload="metadata"
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm text-white/60">
                ไม่มีวิดีโอ
              </div>
            )}
          </div>

          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <p className="text-sm text-muted-foreground">Caption</p>
              <p className="text-sm text-black whitespace-pre-wrap">
                {trend.caption || '-'}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {(trend.hashtags || []).map((tag) => (
                <Badge key={tag} variant="secondary">
                  #{tag.replace(/^#/, '')}
                </Badge>
              ))}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Info label="สถานะ" value={trend.status} />
              <Info label="Privacy" value={trend.privacy || '-'} />
              <Info label="ผู้โพสต์" value={trend.user?.displayName || '-'} />
              <Info label="User ID" value={trend.user?.uid || '-'} />
              <Info label="สร้างเมื่อ" value={fDateTime(trend.createdAt)} />
              <Info label="อัปเดตเมื่อ" value={fDateTime(trend.updatedAt)} />
              <Info label="Views" value={`${trend.viewCount || 0}`} />
              <Info label="Likes" value={`${trend.likeCount || 0}`} />
              <Info label="Comments" value={`${trend.commentCount || 0}`} />
              <Info label="Shares" value={`${trend.shareCount || 0}`} />
            </div>

            {trend.sound?.title && (
              <Info
                label="เสียง"
                value={[trend.sound.title, trend.sound.artist]
                  .filter(Boolean)
                  .join(' - ')}
              />
            )}

            {trend.processingError && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                <p className="text-sm font-medium text-destructive">
                  Processing error
                </p>
                <p className="mt-1 text-sm text-destructive/80">
                  {trend.processingError}
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-medium text-black break-all">{value}</p>
    </div>
  )
}
